import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  CircularProgress,
  Alert,
  Stack,
  Tabs,
  Tab,
  Autocomplete,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip as MuiTooltip,
} from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts'
import { format } from 'date-fns'
import { zhTW } from 'date-fns/locale'
import DownloadIcon from '@mui/icons-material/Download'
import SaveIcon from '@mui/icons-material/Save'
import PersonIcon from '@mui/icons-material/Person'
import { useAnalyticsStore } from '../../stores/analyticsStore'
import { useUserStore } from '../../stores/userStore'
import { exportPageViewStats, exportSessionStats } from '../../utils/excelExport'
import { getScreenLabel, getScreenNavPath, SCREEN_PAGE_INFO } from '../../utils/screenNames'
import { formatDuration } from '../../utils/formatDuration'
import { User } from '../../models/User'
import VideoStatsPanel from './VideoStatsPanel'
import PageUserBreakdownPanel from './PageUserBreakdownPanel'
import EventStatsPanel from './EventStatsPanel'

const COLORS = [
  '#2196F3', '#4CAF50', '#FF9800', '#E91E63', '#9C27B0',
  '#00BCD4', '#FF5722', '#607D8B', '#795548', '#3F51B5',
]

const IGNORED_SCREENS = new Set(['main', 'splash'])

export default function AnalyticsPage() {
  const navigate = useNavigate()
  const {
    pageViewStats,
    sessionRecords,
    idleTimeoutMinutes,
    loading,
    error,
    fetchPageViewStats,
    fetchIdleTimeout,
    updateIdleTimeout,
    fetchAllSessions,
  } = useAnalyticsStore()

  const {
    users,
    selectedUser,
    usageStats,
    fetchUsers,
    fetchUser,
    fetchUserUsageStats,
    fetchUserScreenLogs,
    clearSelectedUser,
  } = useUserStore()

  const [tab, setTab] = useState(0)
  const [timeoutInput, setTimeoutInput] = useState(idleTimeoutMinutes)
  const [saving, setSaving] = useState(false)
  const [pickedUser, setPickedUser] = useState<User | null>(null)

  useEffect(() => {
    fetchPageViewStats()
    fetchIdleTimeout()
    fetchAllSessions()
    fetchUsers()
  }, [fetchPageViewStats, fetchIdleTimeout, fetchAllSessions, fetchUsers])

  useEffect(() => {
    setTimeoutInput(idleTimeoutMinutes)
  }, [idleTimeoutMinutes])

  // 切換選定使用者時抓詳細資料
  useEffect(() => {
    if (pickedUser) {
      fetchUser(pickedUser.id)
      fetchUserUsageStats(pickedUser.id)
      fetchUserScreenLogs(pickedUser.id, 50)
    } else {
      clearSelectedUser()
    }
  }, [pickedUser?.id])

  const handleSaveTimeout = async () => {
    setSaving(true)
    await updateIdleTimeout(timeoutInput)
    setSaving(false)
  }

  // Page view stats rows
  const pageViewRows = pageViewStats
    ? Object.entries(pageViewStats).map(([screen, data], index) => ({
        id: index,
        screenName: screen,
        navPath: getScreenNavPath(screen),
        totalViews: data.totalViews,
        totalDurationSeconds: data.totalDurationSeconds,
        avgDurationSeconds:
          data.totalViews > 0
            ? Math.round(data.totalDurationSeconds / data.totalViews)
            : 0,
      }))
    : []

  const pageViewColumns: GridColDef[] = [
    {
      field: 'screenName',
      headerName: '頁面名稱',
      width: 160,
      renderCell: (params) => getScreenLabel(params.value),
    },
    {
      field: 'navPath',
      headerName: '導覽路徑',
      width: 280,
      renderCell: (params) => (
        <Typography variant="caption" color="text.secondary">
          {params.value}
        </Typography>
      ),
    },
    { field: 'totalViews', headerName: '總瀏覽量', width: 120, type: 'number' },
    {
      field: 'totalDurationSeconds',
      headerName: '總瀏覽時間',
      width: 150,
      renderCell: (params) => formatDuration(params.value),
    },
    {
      field: 'avgDurationSeconds',
      headerName: '平均瀏覽時間',
      width: 150,
      renderCell: (params) => formatDuration(params.value),
    },
  ]

  // Session records rows
  const sessionRows = sessionRecords.map((s, index) => ({
    id: index,
    userName: s.userName || s.userId,
    startedAt: s.startedAt,
    endedAt: s.endedAt,
    durationSeconds: s.durationSeconds,
    isActive: s.isActive,
    closedBy: s.closedBy,
  }))

  const closedByLabel = (value?: string): string => {
    switch (value) {
      case 'idle_timeout': return '閒置超時'
      case 'logout': return '主動登出'
      case 'app_terminate': return 'App 關閉'
      case 'server_cleanup': return '伺服器清理'
      case 'client_restart': return 'App 重啟'
      case undefined:
      case null:
      case '': return '-'
      default: return value
    }
  }

  const sessionColumns: GridColDef[] = [
    { field: 'userName', headerName: '使用者', width: 150 },
    {
      field: 'startedAt',
      headerName: '開始時間',
      width: 180,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleString('zh-TW') : '-',
    },
    {
      field: 'endedAt',
      headerName: '結束時間',
      width: 180,
      renderCell: (params) =>
        params.value ? new Date(params.value).toLocaleString('zh-TW') : '進行中',
    },
    {
      field: 'durationSeconds',
      headerName: '時長',
      width: 120,
      renderCell: (params) => formatDuration(params.value || 0),
    },
    {
      field: 'isActive',
      headerName: '狀態',
      width: 100,
      renderCell: (params) => (params.value ? '進行中' : '已結束'),
    },
    {
      field: 'closedBy',
      headerName: '結束原因',
      width: 140,
      renderCell: (params) => closedByLabel(params.value),
    },
  ]

  // 個人分析 chart data
  const personalChartData = useMemo(() => {
    if (!usageStats?.screenTimeByPage) return []
    return Object.entries(usageStats.screenTimeByPage)
      .filter(([name]) => !IGNORED_SCREENS.has(name))
      .map(([name, seconds]) => ({
        name: getScreenLabel(name),
        seconds,
        label: formatDuration(seconds),
      }))
      .sort((a, b) => b.seconds - a.seconds)
  }, [usageStats])

  if (loading && !pageViewStats && sessionRecords.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 600 }}>
          數據分析
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Idle Timeout 設定 */}
      <Card sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Session 設定
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <TextField
            label="閒置超時（分鐘）"
            type="number"
            size="small"
            value={timeoutInput}
            onChange={(e) => setTimeoutInput(Number(e.target.value))}
            inputProps={{ min: 1, max: 120 }}
            sx={{ width: 180 }}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={saving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={handleSaveTimeout}
            disabled={saving || timeoutInput === idleTimeoutMinutes}
          >
            儲存
          </Button>
          <Typography variant="body2" color="text.secondary">
            使用者閒置超過此時間即算一次 Session 結束（預設 10 分鐘）
          </Typography>
        </Stack>
      </Card>

      {/* Tabs */}
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="頁面瀏覽統計" />
        <Tab label="Session 紀錄" />
        <Tab label="個人分析" />
        <Tab label="影片統計" />
        <Tab label="頁面使用者明細" />
        <Tab label="CTA 事件統計" />
      </Tabs>

      {/* Tab 0: Page View Stats */}
      {tab === 0 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => {
                if (pageViewStats) exportPageViewStats(pageViewStats)
              }}
              disabled={!pageViewStats || Object.keys(pageViewStats).length === 0}
            >
              匯出 Excel
            </Button>
          </Box>
          <Card>
            <DataGrid
              rows={pageViewRows}
              columns={pageViewColumns}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'totalViews', sort: 'desc' }] },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
              }}
            />
          </Card>

          {/* 頁面導覽說明 */}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2" fontWeight={600}>
                頁面導覽說明（App 內如何抵達各頁面）
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 0 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>頁面名稱</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>追蹤 Key</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>App 內導覽路徑</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {SCREEN_PAGE_INFO.map((row) => (
                    <TableRow key={row.keys} hover>
                      <TableCell>{row.label}</TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                          {row.keys}
                        </Typography>
                      </TableCell>
                      <TableCell>{row.nav}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </AccordionDetails>
          </Accordion>
        </>
      )}

      {/* Tab 1: Session Records */}
      {tab === 1 && (
        <>
          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={() => exportSessionStats(sessionRecords)}
              disabled={sessionRecords.length === 0}
            >
              匯出 Excel
            </Button>
          </Box>
          <Card>
            <DataGrid
              rows={sessionRows}
              columns={sessionColumns}
              initialState={{
                pagination: { paginationModel: { pageSize: 25 } },
                sorting: { sortModel: [{ field: 'startedAt', sort: 'desc' }] },
              }}
              pageSizeOptions={[10, 25, 50]}
              disableRowSelectionOnClick
              autoHeight
              sx={{
                border: 'none',
                '& .MuiDataGrid-cell': { borderBottom: '1px solid #f0f0f0' },
              }}
            />
          </Card>
        </>
      )}

      {/* Tab 2: 個人分析 */}
      {tab === 2 && (
        <>
          <Card sx={{ p: 3, mb: 3 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
              <Autocomplete
                sx={{ flex: 1, minWidth: 280 }}
                options={users}
                value={pickedUser}
                onChange={(_, v) => setPickedUser(v)}
                getOptionLabel={(opt) =>
                  opt ? `${opt.displayName}${opt.email ? ` (${opt.email})` : ''}` : ''
                }
                isOptionEqualToValue={(a, b) => a.id === b.id}
                renderInput={(params) => (
                  <TextField {...params} label="選擇使用者" size="small" />
                )}
              />
              {pickedUser && (
                <Button
                  variant="outlined"
                  startIcon={<PersonIcon />}
                  onClick={() => navigate(`/users/${pickedUser.id}`)}
                >
                  查看完整用戶詳情
                </Button>
              )}
            </Stack>
          </Card>

          {!pickedUser ? (
            <Alert severity="info">請選擇使用者以檢視個人分析。</Alert>
          ) : !selectedUser ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              {/* 使用統計摘要 */}
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          總使用時間
                        </Typography>
                        <MuiTooltip title="App 啟動到背景/結束之間累計的前景時間（users.usageStats.totalScreenTime）。和「各頁面瀏覽時間總和」會有差異：頁面總和只計實際進入過的頁面，且 splash/login/main 不計。">
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </MuiTooltip>
                      </Stack>
                      <Typography variant="h5" fontWeight={600} color="primary">
                        {formatDuration(selectedUser.totalScreenTime)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          登入次數
                        </Typography>
                        <MuiTooltip title="包含 Email 登入與 QR Code 登入；每次成功登入 +1（loginCount）。">
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </MuiTooltip>
                      </Stack>
                      <Typography variant="h5" fontWeight={600} color="primary">
                        {selectedUser.loginCount} 次
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Stack direction="row" spacing={0.5} justifyContent="center" alignItems="center">
                        <Typography variant="body2" color="text.secondary">
                          Session 數
                        </Typography>
                        <MuiTooltip title={`使用者連續使用一段時間算一次 Session；閒置超過 ${idleTimeoutMinutes} 分鐘即結束。可在上方「Session 設定」調整。`}>
                          <InfoOutlinedIcon sx={{ fontSize: 14, color: 'text.disabled' }} />
                        </MuiTooltip>
                      </Stack>
                      <Typography variant="h5" fontWeight={600} color="primary">
                        {usageStats?.totalSessions ?? 0} 次
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Card>
                    <CardContent sx={{ textAlign: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        最後登入
                      </Typography>
                      <Typography variant="h6" fontWeight={500}>
                        {selectedUser.lastLoginAt
                          ? format(selectedUser.lastLoginAt, 'MM/dd HH:mm', {
                              locale: zhTW,
                            })
                          : '-'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* 各頁面停留時間圖表 */}
              {personalChartData.length > 0 ? (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      各頁面停留時間
                    </Typography>
                    <Box sx={{ width: '100%', height: 300 }}>
                      <ResponsiveContainer>
                        <BarChart
                          data={personalChartData}
                          layout="vertical"
                          margin={{ top: 5, right: 30, left: 80, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis
                            type="number"
                            tickFormatter={(v) => formatDuration(v)}
                          />
                          <YAxis type="category" dataKey="name" width={80} />
                          <Tooltip
                            formatter={(value: number) => [
                              formatDuration(value),
                              '停留時間',
                            ]}
                          />
                          <Bar dataKey="seconds" radius={[0, 4, 4, 0]}>
                            {personalChartData.map((_entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </Box>
                  </CardContent>
                </Card>
              ) : (
                <Alert severity="info">該使用者尚無頁面停留資料。</Alert>
              )}
            </>
          )}
        </>
      )}

      {/* Tab 3: 影片統計 */}
      {tab === 3 && <VideoStatsPanel />}

      {/* Tab 4: 頁面使用者明細 */}
      {tab === 4 && <PageUserBreakdownPanel />}

      {/* Tab 5: CTA 事件統計 */}
      {tab === 5 && <EventStatsPanel />}
    </Box>
  )
}
